import * as Sentry from '@sentry/browser'
import { processResponse } from '../../behaviors/remote/process_response'
import $ from 'jquery'
import { jQueryBeforeSendAddCsrfToken } from '~/common/helpers/jquery_ajax'
import { attachDynamicEvent } from '~/common/helpers/dom'
import { finishCartUpdate } from './cart_update_completion'

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  ignoreErrors: [
    'Failed to fetch',
    'Load failed',
    'Google Maps',
    'match the expected pattern',
    'The node to be removed is not a child of this node',
    'Non-Error promise rejection',
    "null (reading '$')",
    "null (reading 'parentElement')",
    'invalid version specified',
  ],
})

export function updateCart() {
  return doUpdate(getBaseCartPath() + '/update_totals')
}

// This one will also update the price list.
export async function updateCartAndPrices() {
  showLoading()
  await doUpdate(getBaseCartPath() + '/update_totals_and_prices')
  hideLoading()
}

// This one will also update the donation fields (and in the future anything else that needs to be updated as a result of price being changed)
// We pass the current_price_id so we can compare the old and newly selected price to check if payment methods would change
export async function updateCartOnPriceChange(options = {}) {
  showLoading()
  let path = getBaseCartPath() + '/update_on_price_change'
  if (options.updatePaymentMethods === false) path += '?skip_payment_methods=1'
  await doUpdate(path)
  hideLoading()
}

export async function updateOnVolumeDiscountSelected(options = {}) {
  showLoading()
  let path = getBaseCartPath() + '/update_on_volume_discount_selected'
  if (options.productId && options.priceId) {
    path += '?volume_discount_product_id=' + options.productId
    path += '&volume_discount_price_id=' + options.priceId
  }
  await doUpdate(path)
  hideLoading()
}

export async function updateCartOnAddressChange() {
  return await doUpdate(getBaseCartPath() + '/update_on_address_change')
}

export async function updateCartOnPostalCodeChange() {
  return await doUpdate(getBaseCartPath() + '/update_on_postal_code_change')
}

export async function updateCartOnEmailChange() {
  // TODO - we need some smart replacement logic here
  showLoading()
  await doUpdate(getBaseCartPath() + '/update_on_email_change')
  hideLoading()
}

export async function updateCartOnBirthDateChange() {
  showLoading()
  await doUpdate(getBaseCartPath() + '/update_on_birth_date_change')
  hideLoading()
}

// Delayed version of updateCartOnEmailChange that sets the data-request-in-progress attribute
// immediately before waiting for the specified delay. This allows test helpers like
// wait_for_order_form_update to properly detect that an update is pending even during the delay period.
export function updateCartOnEmailChangeDelayed(delayMs = 30) {
  const form = document.querySelector('.cart-checkout-details')
  if (form && !form.getAttribute('data-request-in-progress')) {
    form.setAttribute('data-request-in-progress', '1')
  }

  setTimeout(() => {
    updateCartOnEmailChange()
  }, delayMs)
}

export async function updateQuantity() {
  showLoading()
  await doUpdate(getBaseCartPath() + '/update_quantity')
  hideLoading()
}

export async function removeItem(id) {
  showLoading()
  await doUpdate(
    getBaseCartPath() + '/update_quantity' + `?remove_product_id=${id}`
  )
  hideLoading()
}

// options can be { orderBumpId: 123 } in case we're adding an order bump
// this allow us to know which order bump is being added and replace any other order bump with the same product
export async function updateOrderBumps(options) {
  const addedOrderBumpId = options?.orderBumpId
  let path = getBaseCartPath() + '/update_order_bumps'
  showLoading()
  if (addedOrderBumpId) path += `?order_bump_id=${addedOrderBumpId}`
  await doUpdate(path)
  hideLoading()
}

// forContracts true allows us to update strictly the entrants without updating the rest of the cart (e.g. without updating payment form component)
export async function updateEntrants(forContracts = false) {
  showLoading()
  const updated = await doUpdate(
    getBaseCartPath() +
      `/update_entrants${forContracts ? '?for_contracts=1' : ''}`
  )
  hideLoading()
  return updated
}

export async function updateAddressInfo() {
  showLoading()
  await doUpdate(getBaseCartPath() + '/update_address_info')
  hideLoading()
}

// Retain each requested endpoint: a totals refresh cannot stand in for full reconciliation.
const pendingCartUpdates = new WeakMap()

async function doUpdate(path) {
  let form = document.querySelector('.cart-checkout-details')
  const formData = new FormData(form)
  // Seat holds belong to this document, including holds created by a price/quantity change.
  if (form?.dataset?.capacityPageToken) {
    formData.set('page_token', form.dataset.capacityPageToken)
  }

  // In the new cart checkout we have quantity selector for each item, living on a separate form
  // Here, we try to find them, and append their values to the formData
  // which is what we are going to send in the PATCH request
  const cartItemForms = document.querySelectorAll('.js-cart-summary-item-form')
  cartItemForms.forEach((form) => {
    const qtySelector = form.querySelector('.js-cart-item-quantity-select')
    if (qtySelector) {
      formData.append(qtySelector.name, qtySelector.value)
    }
  })

  if (form.getAttribute('data-loading-cart-update')) {
    const pending = (pendingCartUpdates.get(form) || []).filter(
      (queuedPath) => queuedPath !== path
    )
    pending.push(path)
    pendingCartUpdates.set(form, pending)
    form.setAttribute('data-pending-cart-update', '1')
    // Queued, not failed — and it resolved here before this file grew a .catch, so callers that branch on the
    // result must keep seeing a success.
    return true
  }
  if (pendingCartUpdates.get(form)?.length) {
    form.setAttribute('data-pending-cart-update', '1')
  }
  form.dispatchEvent(
    new CustomEvent('cart-customer-draft:request-started', { detail: { path } })
  )
  form.setAttribute('data-loading-cart-update', '1')
  form.setAttribute('data-request-in-progress', '1')

  return (
    fetch(path, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(formData),
    })
      .then((response) => {
        // Not for safety — the catch below handles a failure either way. It's for the report: parsing a 500's
        // empty body raises "Unexpected end of JSON input", which says nothing about what actually went wrong.
        if (!response.ok)
          throw new Error(`Cart update returned ${response.status}`)

        return response.json()
      })
      .then((json) => {
        processResponse(json)
        // Only on success. This is what assert_cart_updated waits on, so counting a render that threw would
        // let a test claiming the cart updated pass over a cart that didn't.
        incrementCartUpdateCounter()
        return true
      })
      // Resolving instead of rejecting is what lets every caller's trailing hideLoading() run. The cost is that
      // a caller can no longer learn of a failure by catching, so the outcome comes back as the resolved value
      // and proceedWithPayment reads it — see the entrants check there.
      .catch((error) => {
        reportFailedCartUpdate(error, path)
        return false
      })
      // Exactly once, however the above went — see cart_update_completion.js. Rendering can throw, and a latch
      // left set here silently kills every later cart update.
      .finally(() =>
        finishCartUpdate(form, {
          retry: () => {
            const pending = pendingCartUpdates.get(form)
            const nextPath = pending?.shift() || path
            if (!pending?.length) pendingCartUpdates.delete(form)
            return doUpdate(nextPath)
          },
        })
      )
  )
}

// A cart update in flight when the buyer leaves is aborted by the browser, and reports as the same
// 'Failed to fetch' a real network drop does. Nobody is owed that update any more, so it is not a failure
// worth a Sentry event — and since these arrive in bursts (every navigation away from a checkout), leaving
// them in is how a signal issue turns into one that gets muted.
//
// pageshow resets it: pagehide also fires on the way into the bfcache, and a buyer who comes back with the
// back button has a live cart again whose failures we do want to hear about.
let pageIsGoingAway = false
window.addEventListener('pagehide', () => {
  pageIsGoingAway = true
})
window.addEventListener('pageshow', () => {
  pageIsGoingAway = false
})

// Reported under our own message rather than via captureException, because ignoreErrors above would eat it.
// Those patterns match partially against exception values, and 'Failed to fetch' / 'Load failed' are exactly
// what a dropped cart update looks like — so the failure we most need to see is the one that gets filtered.
//
// It's routine noise on most pages. Not here: this is the buyer's total quietly falling out of step with what
// they picked, and when we went looking for evidence of a real one, there was nothing to find.
function reportFailedCartUpdate(error, path) {
  if (pageIsGoingAway) return

  Sentry.captureMessage('Cart update failed', {
    level: 'error',
    tags: { cart_update: 'failed' },
    extra: { path, error: String(error), stack: error?.stack },
  })
}

export function incrementCartUpdateCounter() {
  let form = document.querySelector('.cart-checkout-details')
  // cart-update-counter is used for tests
  form.setAttribute(
    'data-cart-update-counter',
    parseInt(form.getAttribute('data-cart-update-counter') || 0, 10) + 1
  )
}

function hasContracts(form) {
  return (
    form.querySelector('[data-contracts--contracts-target="contract"]') ||
    document.querySelector(
      '.tw-modal-containers #cart-contracts [data-contracts--contracts-target="contract"]'
    )
  )
}
function showContracts() {
  return new Promise((resolve) => {
    const contractsController = document.querySelector(
      '[data-controller="contracts--contracts"]'
    )

    if (contractsController) {
      contractsController.dispatchEvent(
        new CustomEvent('contracts:show', { detail: { callback: resolve } })
      )
      return
    } else {
      resolve(true)
    }
  })
}

// Express checkout charges before the buyer has seen the form, so these are collected on the
// completion step instead: participant answers, and the parts of the billing address a wallet
// can't answer — its region is a province where our list holds communities, say. A blank signup
// password is generated and sent in the welcome email.
const deferredExpressCheckoutField =
  /^(?:product_\d+\[entrant\]\[\d+\]\[field_\d+_[^\]]+\](?:\[\])?|purchase\[(?:country_code|address|address_2|city|postal_code|region_code)\]|user\[password\])$/

export const isDeferredExpressCheckoutField = (name) =>
  deferredExpressCheckoutField.test(name)

// The first field standing between the buyer and an express charge, if any: identity, tax and
// price fields must be valid before charging, as must any answer the buyer has already entered.
// The contract check never fires from the checkout — a product whose contract is signed before
// payment renders no wallet at all — and is here so that can never silently stop being true.
export function expressCheckoutBlocker(form) {
  if (hasContracts(form)) return {}

  const field = Array.from(form.elements).find((field) => {
    if (
      field.validity.valueMissing &&
      !field.validity.badInput &&
      isDeferredExpressCheckoutField(field.name)
    )
      return false

    return !field.checkValidity()
  })

  return field ? { field } : null
}

// What the buyer calls the field, for an error that can point at it.
export function fieldLabel(field) {
  return (
    field.labels?.[0]?.textContent?.trim() ||
    field.getAttribute('aria-label')?.trim() ||
    field.placeholder?.trim() ||
    null
  )
}

export async function proceedWithPayment(
  form,
  { expressCheckout = false } = {}
) {
  if (expressCheckout) return !expressCheckoutBlocker(form)

  if (!form.reportValidity()) {
    // Ensure that we're going to be showing *something* if this fails. We shouldn't have hidden fields with HTML
    // validation, but we could...
    let invalidFields = Array.from(form.querySelectorAll(':invalid'))
    let visibleInvalidFields = invalidFields.filter((el) =>
      $(el).is(':visible')
    )
    if (visibleInvalidFields.length == 0) {
      console.log('Found no visible invalid fields on order form submit.')
      // Try to capture Sentry message reporting this error still happens
      Sentry.captureMessage(
        'Found no visible invalid fields on order form submit. Found invalid invisible fields: ' +
          invalidFields.map((el) => el.name)
      )
    }
    return false
  }

  // If the entrants need to sign a pre-purchase contract, update the entrants before showing the contracts modal.
  // This way we ensure the contracts always have the latest entrants data.
  const isOneStepForm = document.querySelector('.cart-checkout-details')
  if (isOneStepForm && hasContracts(form)) {
    // Contracts are rendered from the entrants we just sent, so signing one we failed to send means signing a
    // contract naming the wrong participants. Before this file caught its own failures, a failed update rejected
    // and this branch never ran; throwing keeps that, and processFormSubmission's catch tells the buyer.
    if (!(await updateEntrants(true)))
      throw 'We could not save the participant details. Please try again.'

    form.removeAttribute('data-update-entrants')
    return await showContracts()
  }

  return await showContracts()
}

const loadingClasses = [
  'cart-checkout-page--loading',
  'opacity-50',
  'cursor-wait',
  'pointer-events-none-on-children',
]

export function showLoading() {
  mainContainer().classList.add(...loadingClasses)
}

export function hideLoading() {
  mainContainer().classList.remove(...loadingClasses)
  const submitButton = document.querySelector(
    '.cart-checkout-payment button[type="submit"]'
  )
  // Can't really import Rails from ujs because this file is imported from contexts where it causes all sort of weird issues
  if (submitButton && window.Rails && window.Rails.enableElement) {
    window.Rails.enableElement(submitButton)
  } else if (submitButton) {
    submitButton.disabled = false
    const reenableWith = submitButton.dataset.reenableWith
    const disableWith = submitButton.dataset.disableWith

    if (reenableWith && submitButton.innerText == disableWith) {
      submitButton.innerHTML = reenableWith || 'Submit'
    }
  }
}

export function mainContainer() {
  return document.querySelector('.cart-checkout-page, .content-container')
}

export function getBaseCartPath() {
  return document.querySelector('[data-base-cart-path]').dataset.baseCartPath
}

export function updateCartCount(count) {
  let containers = document.querySelectorAll('.shopping-cart-icon-container')
  containers.forEach((container) => {
    container
      .querySelectorAll('.shopping-cart-button__item-count span')
      .forEach((el) => (el.innerText = count))
    if (count == 0) {
      container.classList.add('shopping-cart-icon-container--empty')
    } else {
      container.classList.remove('shopping-cart-icon-container--empty')
    }
  })
}

export function onPaymentMethodChange(listener) {
  attachDynamicEvent(
    '.cart-checkout-payment .payment-method-or-processor-option input[type=radio]',
    'change',
    listener
  )
}

export function updateCartItemQuantityNonDebounced(item, quantity) {
  const $item = $(item)
  $item.addClass('cart__item--loading')
  $item.closest('.cart').addClass('cart--loading')
  return $.ajax({
    url: $item.data('update-url') + '?old_checkout=true',
    type: 'PATCH',
    data: { cart_item: { quantity } },
    beforeSend: jQueryBeforeSendAddCsrfToken,
    complete: (response) => {
      $item.removeClass('cart__item--loading')
      $item.closest('.cart').removeClass('cart--loading')
      const data = response.responseJSON
      processResponse(data)
      return updateCartCount(data.cartCount)
    },
  })
}
